/**
 * ReferenceDataPage Component (Portfolio)
 *
 * Page for managing portfolio reference data entities: Brokers, Instruments.
 * Displays a tabbed interface with URL-based tab state.
 *
 * Desktop: Horizontal tabs (SettingsLayout provides header)
 * Mobile: Breadcrumb + Select dropdown (own header, SettingsLayout header hidden)
 */

import React, { useEffect, useRef } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';

import {
  Card,
  CardHeader,
  CardBody,
  Tabs,
  Tab,
  Select,
  SelectItem,
  Breadcrumbs,
  BreadcrumbItem,
} from '@enfomentis/ui';
import { useIsDesktop } from '@/hooks';
import { UserInstrumentsTab } from '@/components/shared/InstrumentExplorer/UserInstrumentsTab';

import { BrokersTab } from './Brokers';

/**
 * Valid tab keys for the Portfolio Reference Data screen
 */
type TabKey = 'brokers' | 'instruments';

const VALID_TABS: TabKey[] = ['brokers', 'instruments'];
const DEFAULT_TAB: TabKey = 'brokers';

/**
 * Tab display configuration
 */
const TAB_CONFIG: Record<TabKey, { title: string }> = {
  brokers: {
    title: 'Brokers',
  },
  instruments: {
    title: 'Instruments',
  },
};

/**
 * Type guard to check if a string is a valid TabKey
 */
function isValidTab(tab: string | null): tab is TabKey {
  return tab !== null && VALID_TABS.includes(tab as TabKey);
}

export const ReferenceDataPage: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const isDesktop = useIsDesktop();

  // Ref to track if we've already normalized the URL
  const hasNormalized = useRef(false);

  // Read current tab from URL
  const tabParam = searchParams.get('tab');
  const activeTab: TabKey = isValidTab(tabParam) ? tabParam : DEFAULT_TAB;

  // Normalize URL on mount: set default tab if missing/invalid, clear extra params
  useEffect(() => {
    // Skip if already checked this session
    if (hasNormalized.current) return;

    const currentTab = searchParams.get('tab');
    const hasExtraParams = Array.from(searchParams.keys()).some(key => key !== 'tab');
    const needsNormalization = !isValidTab(currentTab) || hasExtraParams;

    if (needsNormalization) {
      const normalizedTab = isValidTab(currentTab) ? currentTab : DEFAULT_TAB;
      setSearchParams({ tab: normalizedTab }, { replace: true });
    }

    // Mark as checked to prevent re-running on subsequent searchParams changes
    hasNormalized.current = true;
  }, [searchParams, setSearchParams]);

  /**
   * Handle tab change - updates URL with new tab
   */
  const handleTabChange = (key: React.Key): void => {
    const newTab = String(key) as TabKey;
    if (isValidTab(newTab) && newTab !== activeTab) {
      setSearchParams({ tab: newTab }, { replace: true });
    }
  };

  /**
   * Handle navigation back to Portfolio page
   */
  const handleBack = (): void => {
    navigate('/settings/portfolio');
  };

  /**
   * Handle navigation to Settings landing page
   */
  const handleNavigateToSettings = (): void => {
    navigate('/settings');
  };

  /**
   * Render content for the active tab
   */
  const renderTabContent = (): React.ReactNode => {
    switch (activeTab) {
      case 'brokers':
        return <BrokersTab />;
      case 'instruments':
        return <UserInstrumentsTab />;
      default:
        return (
          <div className="text-center py-8">
            <p className="text-text-secondary">
              Content coming soon
            </p>
          </div>
        );
    }
  };

  // Desktop layout: Horizontal tabs (breadcrumbs provided by SettingsLayout)
  if (isDesktop) {
    return (
      <div className="flex flex-col flex-1 min-h-0 gap-4">
        <Tabs
          selectedKey={activeTab}
          onSelectionChange={handleTabChange}
          variant="underlined"
          color="primary"
          aria-label="Reference Data sections"
        >
          {VALID_TABS.map((tabKey) => (
            <Tab key={tabKey} title={TAB_CONFIG[tabKey].title} />
          ))}
        </Tabs>

        {renderTabContent()}
      </div>
    );
  }

  // Mobile layout: Breadcrumb + Select dropdown
  return (
    <Card radius="none" shadow="none" className="h-full flex flex-col">
      <CardHeader className="flex-col items-start pt-1 pb-0 border-b border-default-200">
        {/* Breadcrumb navigation - parent context only */}
        <div className="flex items-center pb-1">
          <Breadcrumbs variant="light" size="sm">
            <BreadcrumbItem
              onPress={handleNavigateToSettings}
              className="text-text-secondary hover:text-text-primary"
              isCurrent={false}
            >
              Settings
            </BreadcrumbItem>
            <BreadcrumbItem
              onPress={handleBack}
              className="text-text-secondary hover:text-text-primary"
              isCurrent={false}
            >
              Portfolio
            </BreadcrumbItem>
          </Breadcrumbs>
        </div>

        {/* Tab selector dropdown - styled like Strategy selector */}
        <Select
          aria-label="Select section"
          selectedKeys={[activeTab]}
          onSelectionChange={(keys) => {
            const selected = Array.from(keys)[0];
            if (selected) handleTabChange(selected);
          }}
          className="mt-1 w-full"
          classNames={{
            trigger: 'bg-transparent shadow-none data-[hover=true]:bg-default-50 px-0 min-h-unit-8',
            value: '!text-text-primary text-lg font-semibold',
          }}
          listboxProps={{
            itemClasses: {
              base: [
                'data-[hover=true]:!text-primary data-[hover=true]:!bg-transparent',
                'data-[selectable=true]:focus:!bg-transparent data-[selectable=true]:focus:!text-primary',
                'data-[selected=true]:!text-primary data-[selected=true]:!bg-transparent',
              ].join(' '),
            },
          }}
        >
          {VALID_TABS.map((tabKey) => (
            <SelectItem key={tabKey}>
              {TAB_CONFIG[tabKey].title}
            </SelectItem>
          ))}
        </Select>
      </CardHeader>

      <CardBody className="flex-1 min-h-0 overflow-y-auto px-3 py-4">
        {renderTabContent()}
      </CardBody>
    </Card>
  );
};
